import React, {useEffect, useState} from 'react';
import {
  getAnonymousRequestToken, getLaunchPayload,
} from '../../../state/guest-thunk';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {useParams} from 'react-router';
import {paynowService} from '../../../services/paynow-service';
import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {
  ApplicationStatusState,
  emptyApplicationStatus, GetError,
} from '../../../../security/model/application-status';
import {
  PatientPaymentSchedule,
} from '../../../../patient/components/models/patient-payment.schedule';
import {logUserOut} from '../../../../security/state/user-thunk';
import {GuestToken} from '../../../../security/services/SSO';
import {EnrollmentApprovalView} from './enrollment-approval-view';
import {NoSigAuthDocApprovalPage} from '../no-sig-auth-doc-approval-page';
import {GENERIC_ERROR_MSG} from '../error-messages';

export interface AuthorizationDocumentStatus {
  authorizationDocumentStatusId: number,
  patientId: number,
  patientChampionId: number | null,
  documentTypeId: number,
  documentStatus: 'created' | 'sent' | 'signed',
  externalSignatureRequestId: string,
  wasPfrAdjustmentSent: null,
  authorizationDocuments: {
    typeOfDoc: 'inst' | 'spa' | 'otp' | 'phi'
    authorizationDocumentsId: number
  }
}

export const PaymentProgramEnrollmentPage = () => {

  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ token?: string }>();

  const [patientEncounter, setPatientEncounter] = useState<PatientEncounter | null>(
      null);
  const [msg, setMsg] = useState<ApplicationStatusState>(
      emptyApplicationStatus);
  const [guestToken, setGuestToken] = useState<GuestToken | null>(null);
  const [authDocStatus, setAuthDocStatus] = useState<AuthorizationDocumentStatus | null>(
      null);
  const [authDocPdfBlob, setAuthDocPdfBlob] = useState<Blob | null>(null)
  const [isAuthDocAlreadyApproved, setIsAuthDocAlreadyApproved] = useState(
      false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {

    const getTokenAndPatientEncounter = async () => {

      /**
       * A temporary measure to log out user session before loading the page.
       * If a patient portal session exists, it interferes with the loading of all guest pages.
       * */
      await dispatch(logUserOut());

      const tokenResponse = await dispatch(getAnonymousRequestToken()).unwrap();

      if (!tokenResponse?.token) {
        throw Error('Error fetching guest token');
      }

      setGuestToken(tokenResponse);

      const launchPayloadResponse: any = await dispatch(getLaunchPayload(({
        'encryptedValue': params.token as string,
        'authToken': tokenResponse.token,
      }))).unwrap();

      if (launchPayloadResponse?.isProcessingError) {
        throw Error('Error decrypting patient encounter id');
      }

      const patientEncounterResponse = await paynowService.getPatientEncounter(
          +launchPayloadResponse?.patientEncounterId,
          tokenResponse.token,
          {cacheMode: 'no-cache'}
      );

      if (
          !patientEncounterResponse ||
          patientEncounterResponse.hasErrors ||
          !patientEncounterResponse.patientEncounterId
      ) {
        throw Error('Error fetching patient encounter');
      }

      const typeOfDoc = patientEncounterResponse?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.paymentFreq ===
      'F' ? 'spa' : 'inst';

      const authDocStatusId = +launchPayloadResponse?.authDocStatusId;

      const selectedAuthDoc = patientEncounterResponse.authorizationDocumentStatus.find(
          (authDoc: AuthorizationDocumentStatus) => {
            if (authDocStatusId) {
              return authDoc.authorizationDocumentStatusId ===
                  authDocStatusId &&
                  authDoc.authorizationDocuments.typeOfDoc === typeOfDoc &&
                  authDoc.documentStatus !== 'signed';
            }

            return authDoc.authorizationDocuments.typeOfDoc ===
                typeOfDoc && authDoc.documentStatus !== 'signed';
          });

      if (!selectedAuthDoc) {
        setIsAuthDocAlreadyApproved(true);
        return;
      }
      checkPaymentProgramData(
          patientEncounterResponse.patientPaymentProgram[0].patientPaymentSchedule);

      setPatientEncounter(patientEncounterResponse);
      setAuthDocStatus(selectedAuthDoc);

      const response = await paynowService.postApi("GET", `/patient-encounter/v2/template/${selectedAuthDoc?.documentTypeId}/export`, null,  tokenResponse.token, 'application/pdf');

      if (!(response instanceof ArrayBuffer)) {
        throw Error('Error fetching authorization document');
      }

      const blob = new Blob([response], { type: "application/pdf" });
      setAuthDocPdfBlob(blob)
    };

    getTokenAndPatientEncounter()
      .catch((err) => {
        console.error(err)
        setHasLoadError(true);
        setMsg(GetError(GENERIC_ERROR_MSG));
      })
      .finally(() => {
        setIsPageLoading(false);
      });

  }, []);

  return (

      <NoSigAuthDocApprovalPage
          isPageLoading={isPageLoading}
          isAuthDocAlreadyApproved={isAuthDocAlreadyApproved}
          areRecordsReady={!!patientEncounter || hasLoadError}
          approvalView={
            <EnrollmentApprovalView
                patientEncounter={patientEncounter}
                selectedAuthDocStatus={authDocStatus}
                guestToken={guestToken}
                setMessage={(msg: ApplicationStatusState) => setMsg(msg)}
                authDocPdfBlob={authDocPdfBlob}
                loadErrorMsg={hasLoadError ? GENERIC_ERROR_MSG : undefined}
            />
          }
          errorMsg={GENERIC_ERROR_MSG}
          msgState={[msg, setMsg]}
          alreadyApprovedText={'This payment program has already been approved.'}
      />
  );
};

const checkPaymentProgramData = (patientPaymentSchedule: PatientPaymentSchedule) => {

  if (!patientPaymentSchedule) {
    console.error('No Payment Schedule exists: ', patientPaymentSchedule);
    throw Error();
  }

  if (patientPaymentSchedule.terms < 0) {
    console.error('Payment Program should not have a negative term value',
        patientPaymentSchedule?.terms);
    throw Error();
  }

};
